import { computed, inject, Injectable, signal } from '@angular/core';
import { EditedCustomRoute, EditedPoint, EditedStatus, GetSuggestedPointsBody } from '../interfaces/edit-route.interface';
import { catchError, map, Observable, throwError } from 'rxjs';
import { GetSuggestedPointsResponse } from '../interfaces/edit-route.response';
import { RestService } from 'src/app/services/rest.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { PointType } from 'src/app/pages/custom-route/interfaces/custom-route.interface';

@Injectable({
  providedIn: 'root'
})
export class EditRouteService {
  private _http = inject(HttpClient);
  private _restService = inject(RestService);
  private _customRouteService = inject(CustomRouteService);
  
  private _customRouteData = computed(() => this._customRouteService.customRouteData());
  public customRouteEditedDataToSend = signal<EditedCustomRoute | null>(null);
  public customRouteEditedDataToRender = signal<EditedCustomRoute | null>(null);
  public initialStateCustomRouteEditedDataToRender = signal<EditedCustomRoute | null>(null);
  
  public getSuggestedPoints(body: GetSuggestedPointsBody) : Observable<GetSuggestedPointsResponse> {
    const url = `${this._restService.apiUrlBase}/api/get-suggested-items`;
    
    return this._http.post<GetSuggestedPointsResponse>(url, body, { observe: 'response' })
      .pipe(
        map((httpResponse) => {
          const status = httpResponse.status;
          const responseBody = httpResponse.body;
          return {
            status,
            ...(responseBody as object)
          } as GetSuggestedPointsResponse;
        }),
        map((httpResponse) => {
          const interestIds = httpResponse.categoryList?.map((category: any) => ({id: category.id, value: category.id, label: category.name }))
          return { ...httpResponse, categoryList: interestIds} as GetSuggestedPointsResponse
        }),
        catchError((httpError: HttpErrorResponse) => {
          console.error(`Error fetching suggested points: ${httpError.error.error}`);
          return throwError(() => new Error('HTTP request failed', { cause: httpError }));
        })
      )
  }
  
  
  /*************************************************************************************
   *  Logic to update the customRouteEditedDataToSend (added, updated, deleted points) *
   *************************************************************************************/
  addPointToEditedCustomRouteData(
    newPoint: EditedPoint, 
    orderIndex: number, 
    dayIndex: number
  ): void {
    if (!this._customRouteData() || !this.customRouteEditedDataToRender()) return;

    const currentData = this.customRouteEditedDataToRender();

    const updatedData: EditedCustomRoute = {
      ...currentData,
      dailyRoutes: currentData.dailyRoutes.map((dailyRoute, idx) => {
        if (idx !== dayIndex) return dailyRoute;

        const updatedPoints = [
          ...dailyRoute.points.slice(0, orderIndex),
          { ...newPoint, status: EditedStatus.ADDED },
          ...dailyRoute.points.slice(orderIndex)
        ];

        return {
          ...dailyRoute,
          points: updatedPoints
        };
      })
    };

    this.customRouteEditedDataToSend.set(updatedData);

    console.log(`📌 Added new point at index ${orderIndex} on day ${dayIndex}:`, newPoint);
    console.log('📝 Updated Edited Custom Route to Send:', this.customRouteEditedDataToSend());
  }
  
  replacePointToEditedCustomRouteData(
    pointToReplace: EditedPoint, 
    pointToBeReplaced: EditedPoint,
    dayIndex: number
  ): void {
    if (!this._customRouteData() || !this.customRouteEditedDataToSend()) return;

    const currentData = this.customRouteEditedDataToSend();
    const dailyRoute = currentData.dailyRoutes[dayIndex];
    if (!dailyRoute) return;

    const realIndex = dailyRoute.points.findIndex(point => point.id === pointToBeReplaced.id);
    if (realIndex === -1) return;

    // Usar realIndex en lugar de orderIndex
    const updatedPoints = [...dailyRoute.points];

    const originalPoint = updatedPoints[realIndex];

    updatedPoints.splice(realIndex, 1,
      {
        ...pointToBeReplaced,
        status: EditedStatus.OLD,
        id: originalPoint.id,
      },
      {
        ...pointToReplace,
        status: EditedStatus.UPDATED,
      }
    );

    const newEditedCustomRoute: EditedCustomRoute = {
      ...currentData,
      dailyRoutes: currentData.dailyRoutes.map((route, i) => i === dayIndex
        ? { ...route, points: updatedPoints }
        : route
      ),
    };

    this.customRouteEditedDataToSend.set(newEditedCustomRoute);

    console.log('Updated Edited Custom Route Data to Send after replacement:', this.customRouteEditedDataToSend());
  }

  deletePointToEditedCustomRouteData(pointId: number, pointType: PointType): void {
    if (!this._customRouteData() || !this.customRouteEditedDataToSend()) return;
    
    const currentData = this.customRouteEditedDataToSend();
    if (!currentData) return;
    
    console.log('Current Edited Custom Route Data To Send before deletion:', currentData);

    const newDailyRoutes = currentData.dailyRoutes.map((dailyRoute) => {      
      const pointToDelete = dailyRoute.points.find(point => point.id === pointId && point.type === pointType);
      if (!pointToDelete) return dailyRoute;  
      
      // If the point is added, remove it from the array
      if(pointToDelete.status === EditedStatus.ADDED) {
        return {
          ...dailyRoute,
          points: dailyRoute.points.filter(point => point.id !== pointId && point.type === pointType)
        };
      }

      // If the point is not added, mark it as deleted
      return {
        ...dailyRoute,
        points: dailyRoute.points.map(point =>
          point.id === pointId && point.type === pointType
            ? { ...point, status: EditedStatus.DELETED }
            : point
        )
      };
    });

    const newEditedCustomRoute: EditedCustomRoute = {
      ...currentData,
      dailyRoutes: newDailyRoutes,
    };

    this.customRouteEditedDataToSend.set(newEditedCustomRoute);

    console.log(`Point with ID ${pointId} marked as deleted`);
    console.log('Updated Edited Custom Route To Send after deletion:', newEditedCustomRoute);
  }
  
  recoverPointToEditedCustomRouteData(pointId: number, pointType: PointType, dayIndex: number): void {
    if (!this._customRouteData() || !this.customRouteEditedDataToSend()) return;

    const currentData = this.customRouteEditedDataToSend();
    
    console.log('Current Edited Custom Route Data To Send before recovery:', currentData);

    const updatedData: EditedCustomRoute = {
      ...currentData,
      dailyRoutes: currentData.dailyRoutes.map((dailyRoute, idx) => {
        if (idx !== dayIndex) return dailyRoute;

        const recoveredPoints = dailyRoute.points.map(point => {
          const isSamePoint = point.id === pointId && point.type === pointType;
          
          // recover the point if it was deleted
          if (isSamePoint && point.status === EditedStatus.DELETED) {
            // Elimina la propiedad status
            const { status, ...rest } = point;
            return { ...rest };
          }
          
          // recover the point if it was updated
          if (isSamePoint && point.status === EditedStatus.UPDATED) {
            // update customRouteEditedDataToSend with the original point
            // 2) recover from the original data the point that has status old
            const updatedPointIndex = dailyRoute.points.findIndex(p => p.id === pointId && p.status === EditedStatus.UPDATED);
            if (updatedPointIndex < 0) return point;
            
            const oldPointIndex = updatedPointIndex - 1;
            if (oldPointIndex < 0) return point;
            
            const oldPoint = dailyRoute.points[oldPointIndex];
            //console.log('Recovered point:', oldPoint);
            
            // 3) remove the status from the old point
            const { status, ...rest } = oldPoint;
            //console.log('Recovered point without status:', { ...rest });
            
            return { ...rest }
          }
          
          // if the point is not deleted or updated, return it as is
          return point;
        });
        
        console.log('Recovered points:', recoveredPoints);
        
        // TODO: ME GUSTARÍA NO TENER QUE HACER ESTO YA QUE NO DEBERÍA LLEGAR DUPLICADOS
        // if there are duplicated points, remove the one with status old
        const uniqueRecoveredPoints = recoveredPoints.filter((point, index, self) =>
          index === self.findIndex((p) => p.id === point.id && p.type === point.type && p.status !== EditedStatus.OLD)
        );
        
        return {
          ...dailyRoute,
          points: uniqueRecoveredPoints
        };
      })
    };

    this.customRouteEditedDataToSend.set(updatedData);

    console.log('🔁 Edited Custom Route to Send after recovery:', this.customRouteEditedDataToSend());
  }
  
  
  /*************************************************************************************
   *  Logic to update the customRouteEditedDataToRender                                *
   *************************************************************************************/
  updateCustomRouteEditedDataToRender(): void {
    const currentData = this.customRouteEditedDataToSend();
    if (!currentData) return;

    const updatedData: EditedCustomRoute = {
      ...currentData,
      dailyRoutes: currentData.dailyRoutes.map(dailyRoute => ({
        ...dailyRoute,
        points: dailyRoute.points.filter(point => point.status !== EditedStatus.OLD)
      }))
    };

    this.customRouteEditedDataToRender.set(updatedData);    
  }
}
